<template>
  <Teleport to="body">
    <div v-if="open" class="mv-sheet-overlay" @click="$emit('close')">
      <div class="mv-sheet" @click.stop>
        <div class="mv-sheet-handle"></div>
        <template v-if="!snoozeMode">
          <div class="mv-sheet-title">Alterar status</div>
          <button v-if="currentStatus !== 'open'" class="mv-sheet-opt" @click="$emit('change-status', 'open')" :disabled="statusLoading">
            <span class="mv-sdot open"></span> Abrir
          </button>
          <button v-if="currentStatus !== 'resolved'" class="mv-sheet-opt" @click="$emit('change-status', 'resolved')" :disabled="statusLoading">
            <span class="mv-sdot resolved"></span> Resolver
          </button>
          <button v-if="currentStatus !== 'pending'" class="mv-sheet-opt" @click="$emit('change-status', 'pending')" :disabled="statusLoading">
            <span class="mv-sdot pending"></span> Pendente
          </button>
          <button class="mv-sheet-opt" @click="$emit('update:snoozeMode', true)">
            <span class="mv-sdot snoozed"></span> Adiar…
          </button>
        </template>
        <template v-else>
          <div class="mv-sheet-title">Adiar por quanto tempo?</div>
          <div class="mv-snz-row">
            <input
              type="number"
              :value="snoozeH"
              @input="$emit('update:snoozeH', Number(($event.target as HTMLInputElement).value))"
              min="0" max="99" class="mv-snz-inp"
            />
            <span class="mv-snz-lbl">h</span>
            <input
              type="number"
              :value="snoozeM"
              @input="$emit('update:snoozeM', Number(($event.target as HTMLInputElement).value))"
              min="0" max="59" class="mv-snz-inp"
            />
            <span class="mv-snz-lbl">min</span>
          </div>
          <div class="mv-snz-hint">0h 0min = até próxima resposta</div>
          <div class="mv-snz-btns">
            <button class="mv-snz-cancel" @click="$emit('update:snoozeMode', false)">Cancelar</button>
            <button class="mv-snz-confirm" @click="$emit('confirm-snooze')" :disabled="statusLoading">Confirmar</button>
          </div>
        </template>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
defineProps<{
  open: boolean
  snoozeMode: boolean
  currentStatus: string | undefined
  statusLoading: boolean
  snoozeH: number
  snoozeM: number
}>()

defineEmits<{
  close: []
  'change-status': [status: string]
  'update:snoozeMode': [value: boolean]
  'update:snoozeH': [value: number]
  'update:snoozeM': [value: number]
  'confirm-snooze': []
}>()
</script>

<style scoped>
/* Compartilhado com o header do chat (MobileChatHeader) e outros sheets */
.mv-sdot {
  width: 8px; height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-block;
}
.mv-sdot.open     { background: #3b82f6; }
.mv-sdot.resolved { background: #22c55e; }
.mv-sdot.pending  { background: #f97316; }
.mv-sdot.snoozed  { background: #d97706; }
.mv-sdot-label { max-width: 62px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* Compartilhado com os demais bottom sheets (Agent/Macro/Labels) */
.mv-sheet-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,.4);
  z-index: 2000;
  display: flex; flex-direction: column; justify-content: flex-end;
}
.mv-sheet {
  background: #fff;
  border-radius: 18px 18px 0 0;
  padding: 12px 0 max(20px, env(safe-area-inset-bottom));
  max-height: 72vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
.mv-sheet-handle {
  width: 36px; height: 4px;
  background: #E5E7EB; border-radius: 2px;
  margin: 0 auto 14px;
}
.mv-sheet-title {
  font-size: 11.5px; font-weight: 700;
  color: #9CA3AF;
  text-transform: uppercase; letter-spacing: .7px;
  padding: 0 18px 8px;
}

.mv-sheet-opt {
  display: flex; align-items: center; gap: 11px;
  width: 100%; padding: 14px 18px;
  background: none; border: none;
  border-bottom: 1px solid #F3F4F6;
  font-size: 15px; font-weight: 600; color: #1A1A1A;
  font-family: 'Poppins', sans-serif;
  cursor: pointer; text-align: left;
  -webkit-tap-highlight-color: transparent;
}
.mv-sheet-opt:active { background: #F7F7F7; }
.mv-sheet-opt:last-child { border-bottom: none; }
.mv-sheet-opt:disabled { opacity: .5; }

/* ══ Snooze picker ══ */
.mv-snz-row {
  display: flex; align-items: center; gap: 8px;
  padding: 12px 18px;
}
.mv-snz-inp {
  width: 62px; text-align: center;
  border: 1.5px solid #E5E7EB; border-radius: 8px;
  padding: 9px 6px;
  font-size: 18px; font-family: 'Poppins', sans-serif; font-weight: 700;
  outline: none; color: #1A1A1A;
}
.mv-snz-inp:focus { border-color: #BC0C24; }
.mv-snz-lbl { font-size: 14px; font-weight: 600; color: #6B7280; }
.mv-snz-hint { font-size: 11.5px; color: #9CA3AF; padding: 0 18px 12px; }
.mv-snz-btns { display: flex; gap: 10px; padding: 8px 18px 0; }
.mv-snz-cancel, .mv-snz-confirm {
  flex: 1; padding: 13px;
  border-radius: 10px;
  font-size: 14px; font-weight: 700;
  font-family: 'Poppins', sans-serif;
  cursor: pointer; border: none;
}
.mv-snz-cancel { background: #F3F4F6; color: #6B7280; }
.mv-snz-cancel:active { background: #E5E7EB; }
.mv-snz-confirm { background: #BC0C24; color: #fff; }
.mv-snz-confirm:disabled { opacity: .5; }
.mv-snz-confirm:not(:disabled):active { background: #73010F; }
</style>
